/**
 * Analytics Service
 * Centralized analytics tracking with type safety and provider abstraction
 * 
 * USAGE:
 * ```typescript
 * import { trackEvent, setAnalyticsUser } from "@/services/analyticsService";
 * 
 * // Track an event
 * trackEvent("case_created", { caseId: "123", jurisdiction: "CA" });
 * 
 * // Set user context
 * setAnalyticsUser("user-123");
 * ```
 * 
 * INITIALIZATION:
 * Call initAnalyticsIfNeeded() once at app startup (e.g., in main.jsx after auth is ready)
 */

import type { Analytics } from "firebase/analytics";
import type { AnalyticsEventName, AnalyticsEventPayload } from "@/types/analytics";
import { ANALYTICS_CONFIG, isAnalyticsEnabled, isAnalyticsDebugMode } from "@/config/analyticsConfig";

/**
 * Module-level singleton for analytics instance
 */
let analyticsInstance: Analytics | null = null;
let isInitialized = false;

/**
 * Initialize analytics if enabled
 * This function is safe to call multiple times (idempotent)
 * 
 * @returns Promise that resolves when initialization is complete (or skipped)
 */
export async function initAnalyticsIfNeeded(): Promise<void> {
  // Skip if already initialized
  if (isInitialized) {
    return;
  }

  // Skip if not in browser
  if (typeof window === "undefined") {
    isInitialized = true;
    return;
  }

  // Skip if analytics is disabled
  if (!isAnalyticsEnabled()) {
    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Analytics] Disabled by configuration");
    }
    isInitialized = true;
    return;
  }

  try {
    // Dynamic import to avoid bloating main bundle
    const { getAnalytics } = await import("firebase/analytics");
    const { app } = await import("@/firebase");

    analyticsInstance = getAnalytics(app);
    isInitialized = true;

    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Analytics] Initialized successfully", {
        provider: ANALYTICS_CONFIG.provider,
        enabled: ANALYTICS_CONFIG.enabled,
      });
    }
  } catch (error) {
    console.warn("[Analytics] Failed to initialize:", error);
    analyticsInstance = null;
    isInitialized = true; // Mark as initialized even on failure to prevent retry loops
  }
}

/**
 * Track an analytics event
 * 
 * @param eventName - Name of the event from AnalyticsEventName type
 * @param payload - Optional event parameters
 * 
 * @example
 * trackEvent("case_created", { jurisdiction: "CA", caseType: "family" });
 */
export function trackEvent(
  eventName: AnalyticsEventName,
  payload?: AnalyticsEventPayload
): void {
  // Skip if analytics not enabled
  if (!isAnalyticsEnabled()) {
    return;
  }

  // Debug logging
  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Analytics] Event:", eventName, payload);
  }

  // Skip if not initialized yet
  if (!isInitialized || !analyticsInstance) {
    if (isAnalyticsDebugMode()) {
      console.warn("[Analytics] Event skipped - not initialized:", eventName);
    }
    return;
  }

  // Log event to provider
  try {
    import("firebase/analytics").then(({ logEvent }) => {
      // Firebase logEvent accepts string event names
      logEvent(analyticsInstance!, eventName as string, payload);
    });
  } catch (error) {
    console.warn("[Analytics] Failed to track event:", eventName, error);
  }
}

/**
 * Set the current user ID for analytics
 * Call this after user authentication to associate events with a user
 * 
 * @param userId - User ID (null to clear)
 * 
 * @example
 * setAnalyticsUser("user-abc123");
 * // Later, on logout:
 * setAnalyticsUser(null);
 */
export function setAnalyticsUser(userId: string | null): void {
  // Skip if analytics not enabled
  if (!isAnalyticsEnabled()) {
    return;
  }

  // Debug logging
  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Analytics] Set user:", userId ? `[User ID: ${userId}]` : "[Cleared]");
  }

  // Skip if not initialized yet
  if (!isInitialized || !analyticsInstance) {
    if (isAnalyticsDebugMode()) {
      console.warn("[Analytics] setAnalyticsUser skipped - not initialized");
    }
    return;
  }

  // Set user ID on provider
  try {
    import("firebase/analytics").then(({ setUserId }) => {
      setUserId(analyticsInstance!, userId);
    });
  } catch (error) {
    console.warn("[Analytics] Failed to set user ID:", error);
  }
}

/**
 * Set user properties for analytics
 * Use this to set demographic or account properties for segmentation
 * 
 * @param properties - Key-value pairs of user properties
 * 
 * @example
 * setUserProperties({ subscription_tier: "pro", user_role: "attorney" });
 */
export function setUserProperties(properties: Record<string, string | number | boolean>): void {
  // Skip if analytics not enabled
  if (!isAnalyticsEnabled()) {
    return;
  }

  // Debug logging
  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Analytics] Set user properties:", properties);
  }

  // Skip if not initialized yet
  if (!isInitialized || !analyticsInstance) {
    if (isAnalyticsDebugMode()) {
      console.warn("[Analytics] setUserProperties skipped - not initialized");
    }
    return;
  }

  // Set properties on provider
  try {
    import("firebase/analytics").then(({ setUserProperties: fbSetUserProperties }) => {
      fbSetUserProperties(analyticsInstance!, properties);
    });
  } catch (error) {
    console.warn("[Analytics] Failed to set user properties:", error);
  }
}

/**
 * Check if analytics is ready to use
 * Useful for conditional logic that depends on analytics availability
 */
export function isAnalyticsReady(): boolean {
  return isInitialized && analyticsInstance !== null && isAnalyticsEnabled();
}
